export interface DesktopRedirectConfig {
  // If redirect present, redirect to this string
  redirect?: string;
}

/**
 * Path match map configuration
 */
export const exclusionPaths: string[] = [
  '/admin/billing',
  'notifications/list',
  'authenticate/*',
  '/requests/*',
  '/rfi-requests/*',
  '/',
  '/admin/templates/organizer',
  '/admin/rfi/templates/*',
  '/rfi/*',
  '/enforced-mfa-configuration',
  '/profile/terms-and-conditions-acceptance',
  '/onboarding/*',
  '/preview/smart-tax-organizer',
  // SmartVault Mirror and future external-source browsers are frontend-only
  // (no legacy refactor equivalent), so the desktop site always owns them.
  '/external/*',
  // The firm app owns `/files` (the new Files page, or the mobile list at narrow widths) and
  // `/files/list`, which folds into `/files`. Handing `/files/list` to legacy loops: legacy's
  // Files route sends the user back here. `/files/details/:id` stays in firm too: the narrow
  // list opens rows there, and legacy has no such route (its viewer is `/files/view/:fileId`).
  // `/files/new` stays legacy-owned on desktop so Add New > File keeps the desktop uploader
  // instead of the shared mobile upload form.
  '/files',
  '/files/list',
  '/files/details/:id',
  // https://liscio.atlassian.net/browse/DEV-5681
  // '/requests', // This can be enabled once the new requests page is ready for firms
  // '/requests/:id',  // This can be enabled once the new single request page is ready for firms
];

/**
 * Conditional exclusion paths
 * A string path map to keep all routes in one place vs declaring in DesktopoSiteRedirect.tsx, etc.
 */
export const conditionalExclusionPaths = {
  /*
   * temporary route for ld devFirmRequestsIndexPage flag.
   * Allows for development under a temporary routes to not disrupt current /requests usage
   */
  elasticSearchRequests: '/elastic-search-requests',
  smartTax: '/smart-tax/*',
  // When v5MessagesFrontend is enabled, exclude messages from desktop redirects
  messages: '/messages/*',
  // When enableDashboardRedesign is enabled, exclude the dashboard from desktop
  // redirects so it renders in the frontend instead of bouncing to legacy
  // (otherwise it ping-pongs with the refactor dashboard redirect).
  dashboard: '/dashboard/*',
};

export const pathMap: { [path: string]: DesktopRedirectConfig } = {
  'requests/new': { redirect: '/requests/new' }, // This can be removed once the 'create new request' page is ready for firms
  'requests/import': { redirect: '/requests/import' }, // This can be removed once the 'import requests' page is ready for firms
  '/authenticate/ml/:id': { redirect: '/magicLink/:id' },
  '/': { redirect: '/' },
  '/messages/list': { redirect: '/all_messages' },
  '/messages/details/:id': { redirect: '/inbox/:id' },

  '/edocs/list': { redirect: '/esign_list' },

  '/files/new': { redirect: '/fileupload/new' },
  '/messages/new': { redirect: '/message/new' },
  '/accounts/list': { redirect: '/accounts' },

  '/contacts/list': { redirect: '/contacts' },

  '/tasks/list': { redirect: '/all_tasks' },
  '/tasks/details/:id': { redirect: '/task/detail/:id' },

  '/invoices/list': { redirect: '/' },
  '/invoices/details/:id': { redirect: '/billing/invoices/:id' },
  '/invoices/billing-history/:parentInvoiceId': {
    redirect: '/billing/invoices/:parentInvoiceId',
  },
  '/dashboard/home': { redirect: '/dashboard' },
  '/admin/users': { redirect: '/admin' },
  '/admin/users/update': { redirect: '/admin' },
  '/admin/templates': { redirect: '/templates' },
  '/admin/templates/message': {
    redirect: '/templates?tab=message_template',
  },
  '/admin/templates/tasks': { redirect: '/templates?tab=task_template' },
  '/admin/templates/edocs': { redirect: '/templates?tab=edoc_template' },
  '/admin/preferences': { redirect: '/preferences' },
  '/admin/integrations': { redirect: '/integrations' },
  '/admin/sms': { redirect: '/sms-admin' },
  '/profile/details': { redirect: '/profile_settings' },
};

export const pathMapRoutes = Object.keys(pathMap).map((path) => ({
  path,
}));

export const exclusionPathMapRoutes = exclusionPaths.map((path) => ({ path }));

/**
 * Defer rendering of these paths to prevent OTP consumption prior to redirect
 */
export const deferredPaths = ['/magicLink/:id', '/ml/:id'];
